/**
 * EAAnalysisContext - Shared State for EA Analysis Results
 *
 * Provides analysis context to the EA Chat Drawer for contextual conversations.
 * When a user asks Q.AI about their EA, this context is injected into the prompt.
 *
 * Usage:
 * 1. EAManagerTab sets context when analysis completes
 * 2. EAChatDrawer reads context to provide informed responses
 * 3. Context clears when user switches files or closes drawer
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT DEFINITION
// ═══════════════════════════════════════════════════════════════════════════

const EAAnalysisContext = createContext(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * EAAnalysisProvider - Wraps components that need access to EA analysis context
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const EAAnalysisProvider = ({ children }) => {
  // Core analysis state
  const [analysisContext, setAnalysisContext] = useState(null);

  /**
   * Set EA analysis context
   * @param {object} context - Analysis context data
   * @param {string} context.fileName - Name of the analyzed EA file
   * @param {string} context.fileType - Type of file (expert, indicator, script)
   * @param {string} context.summary - AI-generated summary
   * @param {object} context.quality - Quality metrics object
   * @param {Array} context.bugs - List of detected bugs
   * @param {Array} context.warnings - List of warnings
   * @param {Array} context.improvements - List of improvement suggestions
   * @param {Array} context.riskFactors - List of risk factors
   * @param {object} context.tradingLogic - Detected trading logic
   * @param {string} context.analysisTimestamp - When analysis was performed
   */
  const setEAContext = useCallback((context) => {
    setAnalysisContext({
      ...context,
      analysisTimestamp: context.analysisTimestamp || new Date().toISOString()
    });
  }, []);

  /**
   * Clear the analysis context
   * Call when switching files or closing the drawer
   */
  const clearEAContext = useCallback(() => {
    setAnalysisContext(null);
  }, []);

  /**
   * Generate a context-aware prompt prefix for Q.AI
   * Injects analysis summary into user queries for better responses
   */
  const getContextPromptPrefix = useCallback(() => {
    if (!analysisContext) return '';

    const { fileName, summary, quality, bugs, tradingLogic } = analysisContext;

    let prefix = `[Context: User is asking about their EA "${fileName}"`;

    if (summary) {
      prefix += `. Summary: ${summary.substring(0, 200)}`;
    }

    if (quality?.overallScore) {
      prefix += `. Quality Score: ${quality.overallScore}/100`;
    }

    if (bugs?.length > 0) {
      prefix += `. Found ${bugs.length} bug(s)`;
    }

    if (tradingLogic?.strategy) {
      prefix += `. Strategy: ${tradingLogic.strategy}`;
    }

    prefix += `] `;

    return prefix;
  }, [analysisContext]);

  /**
   * Generate suggested questions based on analysis results
   * Used for quick action chips in the chat drawer
   */
  const getSuggestedQuestions = useCallback(() => {
    if (!analysisContext) {
      return [
        { id: 'general', label: 'Ask about EAs', prompt: 'Tell me about Expert Advisors in MT5' }
      ];
    }

    const { fileName, bugs, quality, improvements, riskFactors } = analysisContext;
    const suggestions = [];

    // Always add summary question
    suggestions.push({
      id: 'summary',
      label: 'Explain analysis',
      prompt: `Can you explain the analysis results for "${fileName}" in simple terms?`
    });

    // Add bug-related question if bugs found
    if (bugs?.length > 0) {
      suggestions.push({
        id: 'bugs',
        label: `Fix ${bugs.length} bug${bugs.length > 1 ? 's' : ''}`,
        prompt: `How do I fix the ${bugs.length} bug${bugs.length > 1 ? 's' : ''} found in "${fileName}"?`
      });
    }

    // Add quality improvement question if score is low
    if (quality?.overallScore && quality.overallScore < 70) {
      suggestions.push({
        id: 'quality',
        label: 'Improve quality',
        prompt: `What are the most important changes to improve the code quality of "${fileName}"?`
      });
    }

    // Add risk question if risk factors found
    if (riskFactors?.length > 0) {
      suggestions.push({
        id: 'risks',
        label: 'Explain risks',
        prompt: `Can you explain the risk factors identified in "${fileName}" and how to mitigate them?`
      });
    }

    // Add improvements question if improvements suggested
    if (improvements?.length > 0) {
      suggestions.push({
        id: 'improvements',
        label: 'Top improvements',
        prompt: `What are the top 3 improvements I should make to "${fileName}"?`
      });
    }

    return suggestions.slice(0, 4); // Max 4 suggestions
  }, [analysisContext]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // State
    analysisContext,
    hasContext: !!analysisContext,
    fileName: analysisContext?.fileName || null,

    // Actions
    setEAContext,
    clearEAContext,

    // Helpers
    getContextPromptPrefix,
    getSuggestedQuestions
  }), [analysisContext, setEAContext, clearEAContext, getContextPromptPrefix, getSuggestedQuestions]);

  return (
    <EAAnalysisContext.Provider value={contextValue}>
      {children}
    </EAAnalysisContext.Provider>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * useEAAnalysis - Hook to access EA analysis context
 * @returns {object} Context value with state and actions
 * @throws {Error} If used outside of EAAnalysisProvider
 */
export const useEAAnalysis = () => {
  const context = useContext(EAAnalysisContext);

  if (!context) {
    throw new Error('useEAAnalysis must be used within an EAAnalysisProvider');
  }

  return context;
};

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY - Build context from analysis result
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build EAAnalysisContext from API response
 * Normalizes different response formats (PascalCase/camelCase)
 *
 * @param {object} file - Selected file info
 * @param {object} analysis - Analysis result from API
 * @returns {object} Normalized context object
 */
export const buildEAContextFromAnalysis = (file, analysis, fileContent = null) => {
  if (!file || !analysis) return null;

  // DEBUG: Log full analysis object to see what we're working with
  console.log('[buildEAContextFromAnalysis] Raw analysis:', JSON.stringify(analysis, null, 2));
  console.log('[buildEAContextFromAnalysis] Has fileContent:', !!fileContent);
  console.log('[buildEAContextFromAnalysis] File content length:', fileContent?.main_source?.length || 0);

  // Normalize property access (handle both PascalCase and camelCase)
  const get = (obj, keys) => {
    if (!obj) return undefined;
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
  };

  // Try multiple paths to find the data - API may nest it differently
  const qualitySource = analysis.qualityMetrics || analysis.quality || analysis.Quality || {};
  const bugsSource = get(analysis, ['bugs', 'Bugs', 'issues', 'Issues', 'criticalIssues', 'CriticalIssues']) || [];
  const enhancementsSource = get(analysis, ['enhancements', 'Enhancements', 'improvements', 'Improvements', 'suggestions', 'Suggestions']) || [];
  const warningsSource = get(analysis, ['warnings', 'Warnings']) || [];
  const risksSource = get(analysis, ['riskFactors', 'RiskFactors', 'risks', 'Risks', 'tradingRisks', 'TradingRisks']) || [];
  const tradingLogicSource = get(analysis, ['tradingLogic', 'TradingLogic', 'strategy', 'Strategy', 'tradingStrategy', 'TradingStrategy']);
  const summarySource = get(analysis, ['summary', 'Summary', 'overview', 'Overview', 'description', 'Description']) || '';

  const context = {
    fileName: file.name,
    fileType: file.file_type || 'expert',
    summary: summarySource,
    quality: {
      overallScore: get(qualitySource, ['overallScore', 'OverallScore', 'overall', 'score']) || 0,
      readability: get(qualitySource, ['readabilityScore', 'ReadabilityScore', 'readability', 'Readability']) || 0,
      maintainability: get(qualitySource, ['maintainabilityScore', 'MaintainabilityScore', 'maintainability', 'Maintainability']) || 0,
      errorHandling: get(qualitySource, ['errorHandlingScore', 'ErrorHandlingScore', 'errorHandling', 'ErrorHandling']) || 0,
      riskManagement: get(qualitySource, ['riskManagementScore', 'RiskManagementScore', 'riskManagement', 'RiskManagement']) || 0,
      complexity: get(qualitySource, ['complexity', 'Complexity']) || 'unknown'
    },
    bugs: bugsSource,
    warnings: warningsSource,
    improvements: enhancementsSource,
    riskFactors: risksSource,
    tradingLogic: tradingLogicSource
    // P8-D ARCHITECTURE: Source code is NO LONGER sent from frontend
    // Backend (FIX Engine) handles context injection:
    // - QAIController detects EA intent from [EA: filename] prefix
    // - Fetches analysis + source from EAAnalysisCacheService
    // - QAIService applies smart truncation (3000 tokens)
    // Frontend only needs fileName for intent detection
  };

  // DEBUG: Log the built context (source code removed - handled by backend)
  console.log('[buildEAContextFromAnalysis] Built context (P8-D backend handles source code)');
  console.log('[buildEAContextFromAnalysis] Context summary - bugs:', bugsSource?.length || 0,
    'improvements:', enhancementsSource?.length || 0);

  return context;
};

export default EAAnalysisContext;
